/**
 * Extracts structured sections from existing full HTML (for loading saved templates).
 * Uses DOMParser (browser-native) to parse HTML.
 */

import type { SectionValues } from './htmlAssembler';
import type { EditableTemplateType } from './templateSections';
import { TEMPLATE_CONFIGS } from './templateSections';
import { sanitizeEmailHtml } from './emailHtmlSanitizer';

/**
 * Returns the sanitized innerHTML of a <p> element. The sanitizer strips the
 * inline styles the assembler adds for email clients (which would otherwise
 * accumulate and bloat the stored markup on every save).
 */
function paragraphInnerHtml(p: Element): string {
  return sanitizeEmailHtml(p.innerHTML);
}

/**
 * Parses saved HTML template body back into structured section values.
 * Supports both the new table-based layout (data-* attributes) and the legacy CSS-class layout.
 * Falls back to defaults if parsing fails.
 */
export function parseSectionsFromHtml(
  html: string,
  templateType: EditableTemplateType
): SectionValues {
  const config = TEMPLATE_CONFIGS[templateType];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Detect layout: new table-based (data-container) vs legacy (.container)
    const isTableLayout = !!doc.querySelector('[data-container]');

    if (isTableLayout) {
      return parseTableLayout(doc, config);
    }
    return parseLegacyLayout(doc, config);
  } catch {
    return getDefaultSectionsFromConfig(config);
  }
}

/**
 * Parses the new table-based email layout using data-* attributes.
 */
function parseTableLayout(
  doc: Document,
  config: ReturnType<typeof TEMPLATE_CONFIGS[keyof typeof TEMPLATE_CONFIGS]>
): SectionValues {
  // Greeting from [data-greeting]
  const greetingEl = doc.querySelector('[data-greeting]');
  const greeting = greetingEl?.textContent?.trim() || config.defaultGreeting;

  // CTA label from [data-cta] a
  const ctaEl = doc.querySelector('[data-cta] a');
  const ctaLabel = ctaEl?.textContent?.trim() || config.defaultCtaLabel;

  // Closing text from [data-closing]
  const closingEl = doc.querySelector('[data-closing]');
  const closingText = closingEl?.textContent?.trim() || config.defaultClosingText;

  // Body paragraphs: all <p> inside [data-body] that are NOT greeting, closing, or link-text
  const body = doc.querySelector('[data-body]');
  const bodyParagraphs: string[] = [];

  if (body) {
    const allParagraphs = body.querySelectorAll('p');
    for (const p of allParagraphs) {
      // Skip greeting, closing, link-text, and "copy and paste" helper text
      if (p.hasAttribute('data-greeting')) continue;
      if (p.hasAttribute('data-closing')) continue;
      if (p.hasAttribute('data-link-text')) continue;
      if (p.closest('[data-cta]')) continue;
      const text = p.textContent?.trim();
      if (!text) continue;
      if (text.toLowerCase().includes('copy and paste this link')) continue;
      bodyParagraphs.push(paragraphInnerHtml(p));
    }
  }

  const finalParagraphs = bodyParagraphs.length > 0
    ? bodyParagraphs.slice(0, config.maxParagraphs)
    : config.defaultBodyParagraphs;

  return { greeting, bodyParagraphs: finalParagraphs, ctaLabel, closingText };
}

/**
 * Parses the legacy CSS-class-based email layout.
 */
function parseLegacyLayout(
  doc: Document,
  config: ReturnType<typeof TEMPLATE_CONFIGS[keyof typeof TEMPLATE_CONFIGS]>
): SectionValues {
  // Extract greeting from .header h1 or .header h2
  const greetingEl = doc.querySelector('.header h1') || doc.querySelector('.header h2');
  const greeting = greetingEl?.textContent?.trim() || config.defaultGreeting;

  // Extract CTA label from a.button
  const ctaEl = doc.querySelector('a.button');
  const ctaLabel = ctaEl?.textContent?.trim() || config.defaultCtaLabel;

  // Extract closing text from .warning
  const warningEl = doc.querySelector('.warning');
  let closingText = config.defaultClosingText;
  if (warningEl) {
    const warningText = warningEl.textContent?.trim() || '';
    const prefixMatch = warningText.match(/^(?:Important:|Note:)\s*/i);
    closingText = prefixMatch ? warningText.slice(prefixMatch[0].length).trim() : warningText;
  }

  // Extract body paragraphs
  const container = doc.querySelector('.container');
  const bodyParagraphs: string[] = [];

  if (container) {
    const infoBox = container.querySelector('.invitation-info') || container.querySelector('.info-box');
    if (infoBox) {
      const text = infoBox.textContent?.trim() || '';
      if (text) bodyParagraphs.push(sanitizeEmailHtml(infoBox.innerHTML));
    }

    const allParagraphs = container.querySelectorAll(':scope > p');
    for (const p of allParagraphs) {
      const text = p.textContent?.trim();
      if (text && !p.classList.contains('link-text') && !p.closest('.footer') && !p.closest('.warning')) {
        if (text.toLowerCase().includes('copy and paste this link')) continue;
        bodyParagraphs.push(paragraphInnerHtml(p));
      }
    }
  }

  const finalParagraphs = bodyParagraphs.length > 0
    ? bodyParagraphs.slice(0, config.maxParagraphs)
    : config.defaultBodyParagraphs;

  return { greeting, bodyParagraphs: finalParagraphs, ctaLabel, closingText };
}

function getDefaultSectionsFromConfig(
  config: ReturnType<typeof TEMPLATE_CONFIGS[keyof typeof TEMPLATE_CONFIGS]>
): SectionValues {
  return {
    greeting: config.defaultGreeting,
    bodyParagraphs: [...config.defaultBodyParagraphs],
    ctaLabel: config.defaultCtaLabel,
    closingText: config.defaultClosingText,
  };
}

/**
 * Returns the default section values for a given template type.
 */
export function getDefaultSections(templateType: EditableTemplateType): SectionValues {
  const config = TEMPLATE_CONFIGS[templateType];
  return {
    greeting: config.defaultGreeting,
    bodyParagraphs: [...config.defaultBodyParagraphs],
    ctaLabel: config.defaultCtaLabel,
    closingText: config.defaultClosingText,
  };
}
